import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Home as HomeIcon, Store, UserRound, Zap, CheckCircle2, PawPrint, Egg } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { JOURNEY, JOURNEY_IMG, CLUTCH_M, CHICK_PICS, PUPPY_PICS } from "../lib/litterJourneyCopy";
import { JOURNEY_MOCKS } from "../components/journey/JourneyMocks";
import { JourneyShareButton } from "../components/journey/JourneyShareButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const WHO = {
  breeder: { icon: Store, cls: "bg-[#12234A] text-white" },
  shopper: { icon: UserRound, cls: "bg-[#F5551E] text-white" },
  auto: { icon: Zap, cls: "bg-emerald-600 text-white" },
};
const STEP_IMG = { post: "post", alert: "alert", join: "waitlist", arrive: "arrival", email: "photos" };

const WhoChip = ({ who, label }) => {
  const W = WHO[who];
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${W.cls}`}><W.icon size={12} /> {label}</span>;
};

const Step = ({ s, i, c }) => {
  const Mock = JOURNEY_MOCKS[s.id];
  const img = STEP_IMG[s.id];
  return (
    <motion.section id={`step-${s.id}`} data-testid={`journey-step-${s.id}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.45 }}
      className="relative pl-14 sm:pl-20 pb-16 scroll-mt-24">
      <span className="absolute left-0 top-0 h-10 w-10 sm:h-14 sm:w-14 rounded-2xl bg-white border-2 border-[#F5551E] text-[#F5551E] font-display font-extrabold text-lg sm:text-2xl flex items-center justify-center shadow-sm">{i + 1}</span>
      <div className="grid lg:grid-cols-[1fr_1.05fr] gap-8 lg:gap-12 items-start">
        <div>
          <WhoChip who={s.who} label={c.who[s.who]} />
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#12234A] mt-3 leading-tight">{s.h}</h2>
          <p className="text-base text-slate-600 mt-3 leading-relaxed">{s.p}</p>
          {s.bullets && <ul className="mt-4 space-y-2">{s.bullets.map((b) => (
            <li key={b} className="flex gap-2.5 text-sm text-slate-700"><CheckCircle2 size={17} className="shrink-0 mt-0.5 text-[#F5551E]" /> {b}</li>))}</ul>}
          {img && <img src={JOURNEY_IMG[img]} alt={`${c.img_alt}: ${s.h}`} loading="lazy" className="mt-6 w-full aspect-[3/2] object-cover rounded-3xl" data-testid={`journey-img-${s.id}`} />}
        </div>
        {Mock && <div className="lg:sticky lg:top-24"><p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">{c.example}</p><Mock m={c.m} /></div>}
      </div>
    </motion.section>
  );
};

const KindToggle = ({ k, clutch, setClutch }) => (
  <div className="flex items-center gap-3 flex-wrap" data-testid="journey-kind-toggle">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{k.toggle}</span>
    <div className="inline-flex rounded-full bg-white border border-slate-200 p-1">
      {[[false, k.t_litter, PawPrint], [true, k.t_clutch, Egg]].map(([v, label, Icon]) => (
        <button key={label} onClick={() => setClutch(v)} data-testid={`journey-kind-${v ? "clutch" : "litter"}`}
          className={`rounded-full px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${clutch === v ? "bg-[#12234A] text-white" : "text-[#12234A] hover:text-[#F5551E]"}`}>
          <Icon size={14} /> {label}</button>))}
    </div>
  </div>
);

export default function LitterJourney() {
  const { lang } = useLanguage();
  const [clutch, setClutch] = useState(false);
  const base = JOURNEY[lang] || JOURNEY.en;
  const k = CLUTCH_M[lang] || CLUTCH_M.en;
  const c = { ...base, m: clutch ? { ...base.m, ...k, pics: CHICK_PICS } : { ...base.m, pics: PUPPY_PICS } };
  return (
    <div className="bg-[#FBF8F4]" data-testid="litter-journey-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-[#FFF4F0] rounded-full px-3 py-1"><Sparkles size={13} /> {c.badge}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#12234A] mt-4 leading-[1.05]" data-testid="journey-title">{c.title}</h1>
          <p className="text-base text-slate-600 mt-5 max-w-xl leading-relaxed">{c.intro}</p>
          <p className="text-sm text-slate-500 mt-5">{c.legend}</p>
          <div className="flex flex-wrap gap-2 mt-2">{Object.keys(WHO).map((w) => <WhoChip key={w} who={w} label={c.who[w]} />)}</div>
          <div className="mt-6 flex items-center gap-3 flex-wrap"><JourneyShareButton fr={lang === "fr"} />
            <span className="text-xs text-slate-500">{lang === "fr" ? "Éleveurs : partagez ce guide avec vos futures familles." : "Breeders: share this guide with your future families."}</span></div>
        </div>
        <img src={JOURNEY_IMG.arrival} alt={c.img_alt} className="w-full aspect-[3/2] object-cover rounded-[2rem] shadow-xl" />
      </header>

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="journey-toc">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">{c.toc}</p>
        <div className="flex gap-2 overflow-x-auto pb-2">{c.steps.map((s, i) => (
          <a key={s.id} href={`#step-${s.id}`} data-testid={`journey-toc-${s.id}`} className="shrink-0 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#12234A] hover:border-[#F5551E] hover:text-[#F5551E] transition-colors">{i + 1}. {s.h}</a>))}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 relative">
        <div className="sticky top-[6.75rem] z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-10 py-2.5 bg-[#FBF8F4]/95 backdrop-blur-md border-b border-slate-200/60"><KindToggle k={k} clutch={clutch} setClutch={setClutch} /></div>
        <span className="absolute left-[2.25rem] sm:left-[3.25rem] top-2 bottom-16 w-0.5 bg-gradient-to-b from-[#F5551E] via-[#F5551E]/40 to-transparent" aria-hidden />
        {c.steps.map((s, i) => <Step key={s.id} s={s} i={i} c={c} />)}
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="journey-rules">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#12234A]">{c.rules_h}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">{c.rules.map(([h, p]) => (
          <div key={h} className="rounded-2xl bg-white border border-slate-100 p-5"><p className="font-display font-bold text-[#12234A]">{h}</p><p className="text-sm text-slate-600 mt-1.5">{p}</p></div>))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-16" data-testid="journey-faq">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#12234A]">{c.faq_h}</h2>
        <Accordion type="single" collapsible className="mt-4">{c.faq.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`}><AccordionTrigger data-testid={`journey-faq-${i}`} className="text-left text-[#12234A] font-semibold">{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>))}
        </Accordion>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-3xl bg-[#12234A] text-white p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <p className="font-display font-bold text-xl flex items-center gap-2"><HomeIcon size={20} className="text-[#F5551E]" /> {c.cta_text}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/litters" data-testid="journey-browse-cta" className="rounded-xl bg-white/10 hover:bg-white/20 transition-colors px-5 py-2.5 text-sm font-semibold">{c.cta_browse}</Link>
            <Link to="/dashboard?tab=litters" data-testid="journey-post-cta" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] transition-colors px-5 py-2.5 text-sm font-semibold">{c.cta_post}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
